import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, CalendarClock, ArrowLeftRight, Phone, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api, { fmtDate, formatErr } from "../../lib/api";
import { Loading, StatusPill } from "../../components/Shared";
import { useAuth } from "../../context/AuthContext";

function Row({ label, children }) {
  return (
    <div className="flex items-start justify-between gap-4 py-1.5 text-sm">
      <span className="shrink-0 text-stone-500">{label}</span>
      <span className="text-right font-semibold text-stone-800">{children ?? "—"}</span>
    </div>
  );
}

export default function VisitDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const canManage = ["ops_manager", "admin", "super_admin"].includes(user?.role);
  const [d, setD] = useState(null);
  const [cms, setCms] = useState([]);
  const [newTime, setNewTime] = useState("");
  const [newCm, setNewCm] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/admin/visits/${id}`).then((r) => setD(r.data)).catch((e) => { toast.error(formatErr(e)); setD({ error: true }); });
  useEffect(() => {
    load();
    api.get("/admin/care-managers").then((r) => setCms(r.data)).catch(() => {});
  }, [id]);

  if (!d) return <Loading />;
  if (d.error) return <p className="p-6 text-sm text-stone-500" data-testid="visit-error">Visit not found or unavailable.</p>;
  const v = d.visit;
  const rep = v.report || {};
  const vitals = rep.vitals || {};

  const run = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast.success(msg); await load(); }
    catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  const reschedule = () => {
    if (!newTime) { toast.error("Pick a new date & time"); return; }
    run(() => api.post(`/admin/visits/${id}/reschedule`, { scheduled_at: newTime }), "Visit rescheduled — family & Care Manager notified");
  };
  const reassign = () => {
    if (!newCm) { toast.error("Select a Care Manager"); return; }
    run(() => api.post(`/admin/visits/${id}/reassign`, { cm_id: newCm }), "Visit reassigned — family & Care Manager notified");
  };

  return (
    <div data-testid="visit-detail">
      <Link to="/app/admin" className="mb-3 inline-flex items-center gap-1 text-sm font-semibold text-emerald-900" data-testid="visit-back">
        <ArrowLeft className="h-4 w-4" /> Today
      </Link>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-heading text-2xl font-bold tracking-tight text-stone-900">Visit — {d.parent?.name || "Parent"}</h1>
        <StatusPill status={v.status} />
      </div>
      {v.late && (
        <p className="mt-3 flex items-center gap-2 rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm font-semibold text-amber-900" data-testid="visit-late-banner">
          <AlertTriangle className="h-4 w-4" /> This visit is late / missed its scheduled time — coordinate with the Care Manager.
        </p>
      )}

      <div className="mt-6 grid gap-5 lg:grid-cols-2">
        <div className="space-y-5">
          <div className="p1-card p-5" data-testid="visit-info">
            <h2 className="font-heading text-base font-semibold text-stone-900">Visit details</h2>
            <div className="mt-2 divide-y divide-stone-50">
              <Row label="Family">
                <Link to={`/app/admin/customers?id=${v.family_id}`} className="text-emerald-900 underline" data-testid="visit-family-link">{d.family?.name || "—"}</Link>
              </Row>
              <Row label="Parent">{d.parent?.name}</Row>
              <Row label="Area">{d.parent ? `${d.parent.zone || ""}${d.parent.city ? `, ${d.parent.city}` : ""}` : "—"}</Row>
              <Row label="Care Manager">{d.care_manager?.name || v.cm_name || "—"}</Row>
              <Row label="Scheduled">{fmtDate(v.scheduled_at)}</Row>
              <Row label="Purpose">{v.purpose || "—"}</Row>
              <Row label="Check-in">{v.check_in_at ? fmtDate(v.check_in_at) : "—"}</Row>
              <Row label="Check-out">{v.check_out_at ? fmtDate(v.check_out_at) : "—"}</Row>
            </div>
            {d.owner?.phone && (
              <a href={`tel:${d.owner.phone}`} className="mt-3 inline-flex items-center gap-1 rounded-full bg-emerald-900 px-3 py-1.5 text-xs font-bold text-white" data-testid="visit-call-family">
                <Phone className="h-3.5 w-3.5" /> Call family — {d.owner.name}
              </a>
            )}
          </div>

          {canManage && v.status === "scheduled" && (
            <div className="p1-card p-5" data-testid="visit-actions">
              <h2 className="font-heading text-base font-semibold text-stone-900">Coordinate visit</h2>
              <div className="mt-3 space-y-3">
                <div>
                  <label className="p1-label"><CalendarClock className="mr-1 inline h-3.5 w-3.5" /> Reschedule to</label>
                  <div className="flex gap-2">
                    <input type="datetime-local" className="p1-input flex-1" min={new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16)} value={newTime} onChange={(e) => setNewTime(e.target.value)} data-testid="visit-reschedule-at" />
                    <button onClick={reschedule} disabled={busy} className="rounded-xl bg-emerald-900 px-4 py-2 text-sm font-bold text-white disabled:opacity-50" data-testid="visit-reschedule-btn">Save</button>
                  </div>
                </div>
                <div>
                  <label className="p1-label"><ArrowLeftRight className="mr-1 inline h-3.5 w-3.5" /> Reassign Care Manager</label>
                  <div className="flex gap-2">
                    <select className="p1-input flex-1" value={newCm} onChange={(e) => setNewCm(e.target.value)} data-testid="visit-reassign-cm">
                      <option value="">— Select Care Manager —</option>
                      {cms.map((c) => <option key={c.id} value={c.id}>{c.name} ({c.assigned_families}/{c.max_families})</option>)}
                    </select>
                    <button onClick={reassign} disabled={busy} className="rounded-xl border border-emerald-800 px-4 py-2 text-sm font-bold text-emerald-900 disabled:opacity-50" data-testid="visit-reassign-btn">Reassign</button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="p1-card p-5" data-testid="visit-report">
          <h2 className="font-heading text-base font-semibold text-stone-900">Visit report</h2>
          {!rep.observations && !rep.note && !(rep.tasks_completed || []).length ? (
            <p className="mt-3 text-sm text-stone-400">Report appears here after the Care Manager checks out.</p>
          ) : (
            <div className="mt-3 space-y-3 text-sm">
              {(rep.tasks_completed || []).length > 0 && (
                <div>
                  <p className="p1-label">Tasks completed</p>
                  <div className="mt-1 flex flex-wrap gap-1.5">
                    {rep.tasks_completed.map((t, i) => <span key={i} className="rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-900">{t}</span>)}
                  </div>
                </div>
              )}
              {Object.keys(vitals).length > 0 && (
                <div>
                  <p className="p1-label">Vitals</p>
                  <p className="mt-1 text-stone-700">{Object.entries(vitals).map(([k, val]) => `${k.toUpperCase()}: ${val}`).join(" · ")}</p>
                </div>
              )}
              {rep.observations && <div><p className="p1-label">Observations</p><p className="mt-1 text-stone-700">{rep.observations}</p></div>}
              {rep.note && <div><p className="p1-label">Care Manager note</p><p className="mt-1 text-stone-700">{rep.note}</p></div>}
              {(rep.followups || []).length > 0 && (
                <div><p className="p1-label">Follow-ups</p>
                  <ul className="mt-1 list-disc pl-5 text-stone-700">{rep.followups.map((f, i) => <li key={i}>{f}</li>)}</ul>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
